import { createTheme, type Theme } from '@mui/material/styles';

/**
 * Tema MUI v6 — Tilde brand (Slice A — rebrand de "AFIP Platform" a Tilde).
 *
 * Design source: `/tmp/afip-design-v2/afip/project/src/theme.jsx` + `brand.jsx`.
 *
 * Decisiones:
 *  - Paleta Tilde: indigo (#4F46FF) como primary + citrus (#F4C430) como secondary.
 *  - Dark mode: indigoLight (#7C76FF) levanta como `primary.main` para contraste sobre
 *    backgrounds oscuros; deep indigo (#4F46FF) queda como `dark` shade. Background
 *    `default` deep indigo (#0A0F22), `paper` mid (#141A33), `paperElevated` (#1B2240).
 *  - Light mode: indigo (#4F46FF) full saturation; background warm paper (#F8F4E9 ≡
 *    BRAND.paperWarm en la spec, paperSoft en el design v2 — sustituye al neutro
 *    #f6f7f9 del Phase 1). Text ink (#0E1326).
 *  - Mantenemos typography Roboto, shape.borderRadius 8 y la escala de shadows
 *    granular del Phase 1 (25 slots) actualizada a tonos azulados en dark.
 *  - `paperElevated` se inyecta vía `MuiMenu`/styleOverrides como antes.
 */
export function buildTheme(mode: 'dark' | 'light'): Theme {
  const dark = mode === 'dark';

  const background = dark
    ? {
        default: '#0A0F22',
        paper: '#141A33',
        paperElevated: '#1B2240',
      }
    : {
        default: '#F8F4E9',
        paper: '#FFFFFF',
        paperElevated: '#FFFFFF',
      };

  const primary = dark
    ? { main: '#7C76FF', dark: '#4F46FF', light: '#B6B2FF', contrastText: '#FAFAF5' }
    : { main: '#4F46FF', dark: '#3A33D6', light: '#7C76FF', contrastText: '#FFFFFF' };

  const secondary = dark
    ? { main: '#F4C430', dark: '#D4A20F', light: '#FFE082', contrastText: '#0E1326' }
    : { main: '#D4A20F', dark: '#B08A00', light: '#F4C430', contrastText: '#0E1326' };

  const divider = dark ? 'rgba(255,255,255,0.10)' : 'rgba(14,19,38,0.10)';
  const hoverBg = dark ? 'rgba(255,255,255,0.08)' : 'rgba(14,19,38,0.04)';
  const selectedBg = dark ? 'rgba(124,118,255,0.16)' : 'rgba(79,70,255,0.08)';

  const textPrimary = dark ? 'rgba(250,250,245,0.92)' : '#0E1326';
  const textSecondary = dark ? 'rgba(250,250,245,0.62)' : 'rgba(14,19,38,0.62)';
  const textDisabled = dark ? 'rgba(250,250,245,0.38)' : 'rgba(14,19,38,0.38)';

  // Shadows scale — 25 slots. Tonos azulados en dark, suaves grises en light.
  const customShadows = (dark
    ? [
        'none',
        '0 1px 2px rgba(0,0,0,0.6), 0 1px 1px rgba(0,0,0,0.4)',
        '0 1px 2px rgba(0,0,0,0.6), 0 1px 1px rgba(0,0,0,0.4)',
        '0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 2px 4px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 4px 8px rgba(0,0,0,0.5), 0 6px 12px rgba(0,0,0,0.4)',
        '0 8px 16px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 8px 16px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 8px 16px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 8px 16px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.4)',
        '0 12px 20px rgba(0,0,0,0.5), 0 6px 10px rgba(0,0,0,0.4)',
        '0 12px 20px rgba(0,0,0,0.5), 0 6px 10px rgba(0,0,0,0.4)',
        '0 12px 20px rgba(0,0,0,0.5), 0 6px 10px rgba(0,0,0,0.4)',
        '0 16px 28px rgba(0,0,0,0.5), 0 8px 14px rgba(0,0,0,0.4)',
        '0 16px 28px rgba(0,0,0,0.5), 0 8px 14px rgba(0,0,0,0.4)',
        '0 16px 28px rgba(0,0,0,0.5), 0 8px 14px rgba(0,0,0,0.4)',
        '0 20px 32px rgba(0,0,0,0.55), 0 9px 24px rgba(0,0,0,0.5)',
        '0 20px 32px rgba(0,0,0,0.55), 0 9px 24px rgba(0,0,0,0.5)',
        '0 20px 32px rgba(0,0,0,0.55), 0 9px 24px rgba(0,0,0,0.5)',
        '0 20px 32px rgba(0,0,0,0.55), 0 9px 24px rgba(0,0,0,0.5)',
        '0 24px 38px rgba(0,0,0,0.55), 0 9px 46px rgba(0,0,0,0.5)',
        '0 24px 38px rgba(0,0,0,0.55), 0 9px 46px rgba(0,0,0,0.5)',
        '0 24px 38px rgba(0,0,0,0.55), 0 9px 46px rgba(0,0,0,0.5)',
      ]
    : [
        'none',
        '0 1px 2px rgba(14,19,38,0.06), 0 1px 3px rgba(14,19,38,0.08)',
        '0 1px 2px rgba(14,19,38,0.06), 0 1px 3px rgba(14,19,38,0.08)',
        '0 2px 4px rgba(14,19,38,0.08), 0 4px 6px rgba(14,19,38,0.05)',
        '0 2px 4px rgba(14,19,38,0.08), 0 4px 6px rgba(14,19,38,0.05)',
        '0 2px 4px rgba(14,19,38,0.08), 0 4px 6px rgba(14,19,38,0.05)',
        '0 2px 4px rgba(14,19,38,0.08), 0 4px 6px rgba(14,19,38,0.05)',
        '0 4px 8px rgba(14,19,38,0.08), 0 6px 10px rgba(14,19,38,0.05)',
        '0 6px 12px rgba(14,19,38,0.08), 0 10px 20px rgba(14,19,38,0.06)',
        '0 6px 12px rgba(14,19,38,0.08), 0 10px 20px rgba(14,19,38,0.06)',
        '0 6px 12px rgba(14,19,38,0.08), 0 10px 20px rgba(14,19,38,0.06)',
        '0 6px 12px rgba(14,19,38,0.08), 0 10px 20px rgba(14,19,38,0.06)',
        '0 10px 18px rgba(14,19,38,0.10), 0 12px 24px rgba(14,19,38,0.08)',
        '0 10px 18px rgba(14,19,38,0.10), 0 12px 24px rgba(14,19,38,0.08)',
        '0 10px 18px rgba(14,19,38,0.10), 0 12px 24px rgba(14,19,38,0.08)',
        '0 12px 22px rgba(14,19,38,0.12), 0 14px 28px rgba(14,19,38,0.10)',
        '0 12px 22px rgba(14,19,38,0.12), 0 14px 28px rgba(14,19,38,0.10)',
        '0 12px 22px rgba(14,19,38,0.12), 0 14px 28px rgba(14,19,38,0.10)',
        '0 14px 24px rgba(14,19,38,0.14), 0 12px 28px rgba(14,19,38,0.12)',
        '0 14px 24px rgba(14,19,38,0.14), 0 12px 28px rgba(14,19,38,0.12)',
        '0 14px 24px rgba(14,19,38,0.14), 0 12px 28px rgba(14,19,38,0.12)',
        '0 14px 24px rgba(14,19,38,0.14), 0 12px 28px rgba(14,19,38,0.12)',
        '0 14px 28px rgba(14,19,38,0.18), 0 10px 10px rgba(14,19,38,0.14)',
        '0 14px 28px rgba(14,19,38,0.18), 0 10px 10px rgba(14,19,38,0.14)',
        '0 14px 28px rgba(14,19,38,0.18), 0 10px 10px rgba(14,19,38,0.14)',
      ]) as unknown as Theme['shadows'];

  return createTheme({
    palette: {
      mode,
      primary,
      secondary,
      background,
      divider,
      text: {
        primary: textPrimary,
        secondary: textSecondary,
        disabled: textDisabled,
      },
      success: dark
        ? { main: '#66bb6a', dark: '#388e3c', light: '#81c784' }
        : { main: '#2e7d32', dark: '#1b5e20', light: '#4caf50' },
      warning: dark
        ? { main: '#ffa726', dark: '#f57c00', light: '#ffb74d' }
        : { main: '#ed6c02', dark: '#e65100', light: '#ff9800' },
      error: dark
        ? { main: '#f44336', dark: '#d32f2f', light: '#e57373' }
        : { main: '#d32f2f', dark: '#c62828', light: '#ef5350' },
      info: dark
        ? { main: '#29b6f6', dark: '#0288d1', light: '#4fc3f7' }
        : { main: '#0288d1', dark: '#01579b', light: '#03a9f4' },
      action: {
        hover: hoverBg,
        selected: selectedBg,
      },
    },
    // Radios alineados a la escala de tokens del design system (Manual v0.1): r-md 12 como
    // base de superficies, con overrides puntuales por componente (botones, cards, inputs).
    shape: { borderRadius: 12 },
    shadows: customShadows,
    typography: {
      // Skin de marca Tilde: Space Grotesk (display/UI) — JetBrains Mono se aplica vía
      // `var(--font-mono)` en datos técnicos (CUIT, Client ID, CAE, code blocks).
      fontFamily:
        '"Space Grotesk", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      h1: { fontWeight: 700, letterSpacing: '-0.025em' },
      h2: { fontWeight: 700, letterSpacing: '-0.022em' },
      h3: { fontWeight: 600, letterSpacing: '-0.02em' },
      h4: { fontWeight: 600, letterSpacing: '-0.018em' },
      h5: { fontWeight: 600, letterSpacing: '-0.015em' },
      h6: { fontWeight: 600, letterSpacing: '-0.012em' },
      subtitle1: { fontWeight: 500 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '-0.01em' },
      // Eyebrows / overlines en mono (JetBrains) — patrón "eyebrow pill" del Manual de Marca.
      overline: {
        fontFamily: 'var(--font-mono)',
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${divider}`,
            borderRadius: 16,
            transition: 'border-color 0.2s, transform 0.2s',
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 7, fontWeight: 600 } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 12 },
          sizeSmall: { borderRadius: 9 },
          sizeLarge: { borderRadius: 16 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
      MuiToggleButton: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } },
      },
      // Headers de tabla con el look "data-table" del Manual: mono, uppercase, muted.
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontFamily: 'var(--font-mono)',
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: textSecondary,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontSize: 12, fontWeight: 500, padding: '7px 11px' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { backgroundColor: background.paperElevated },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: 'none', borderRight: `1px solid ${divider}` },
        },
      },
      MuiAppBar: {
        defaultProps: { color: 'default', elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', borderBottom: `1px solid ${divider}` },
        },
      },
    },
  });
}
